import { api } from "@/features/utils/fetch-server";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

/**
 * The booking calendar the agent opened in the chat (`show_booking_calendar`). Mirrors
 * the backend's `tools/calendar/booking-calendar.ts`: the steps before the days, a page of
 * days and times, and the booking itself — done by the visitor, without the model.
 */
export interface BookingCalendarPerformer {
  id: string;
  name: string;
}

/**
 * What a visitor who is not signed in gives to book: the SMS code (`verified`), or a
 * first and last name, a phone and an e-mail, typed and not checked (`declarative`). The
 * tenant's setting.
 */
export type ChatBookingVerification = "verified" | "declarative";

export interface BookingCalendarOptions {
  call_id: string;
  verification: ChatBookingVerification;
  /** Set when the agent opened the calendar on one service: that step is skipped. */
  service_id: string | null;
  performer_id: string | null;
  /** An appointment they already have coming up: shown instead of the calendar. */
  existing_event_id: string | null;
  services: {
    id: string;
    name: string;
    duration: number;
    price?: number;
    performers: BookingCalendarPerformer[];
  }[];
}

export interface BookingCalendarTime {
  /** Epoch ms. */
  start: number;
  /** "HH:mm", already in the calendar's timezone. */
  time: string;
  performers?: string[];
}

export interface BookingCalendarDay {
  /** "YYYY-MM-DD" in the calendar's timezone. */
  date: string;
  times: BookingCalendarTime[];
}

export interface BookingCalendarPage {
  call_id: string;
  service: { id: string; name: string; duration: number };
  performer: BookingCalendarPerformer | null;
  timezone: string;
  language?: string;
  from: string;
  to: string;
  days: BookingCalendarDay[];
  next: string | null;
}

/** Why a booking did not go through — the backend's `BookingCalendarFailure`. */
export type BookingCalendarFailure =
  | "not_found"
  | "sign_in_required"
  | "name_required"
  | "contact_required"
  | "already_booked"
  | "refused"
  | "failed";

const base = (publicId: string, callId: string) =>
  `/api/sessions/v1/public/${publicId}/booking-calendar/${encodeURIComponent(callId)}`;

export function useBookingCalendarOptions(
  publicId: string,
  callId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["public-booking-calendar-options", publicId, callId],
    queryFn: (): Promise<BookingCalendarOptions> =>
      api.get(`${base(publicId, callId)}/options`, { credentials: "include" }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Read live, never from the tool message: the times move as other people book, and the
 * visitor pages through weeks the agent never looked at.
 */
export function useBookingCalendarPage(
  publicId: string,
  callId: string,
  query: { service_id?: string; performer_id?: string; from?: string },
  enabled: boolean,
) {
  const params = new URLSearchParams(
    Object.entries(query).filter(([, v]) => !!v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: ["public-booking-calendar", publicId, callId, params],
    queryFn: (): Promise<BookingCalendarPage> =>
      api.get(`${base(publicId, callId)}${params ? `?${params}` : ""}`, {
        credentials: "include",
      }),
    enabled: enabled && !!query.service_id,
    // Paging keeps the previous week on screen until the next one lands: the card must
    // not collapse to a spinner and jump the thread on every click.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** Book the time clicked. The confirmation reaches the thread over the socket. */
export function useBookFromCalendar(publicId: string, callId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      service_id: string;
      performer_id?: string;
      start: number;
    }): Promise<{ ok: true; event_id?: string; status: string }> =>
      api.post(`${base(publicId, callId)}/book`, body, {
        credentials: "include",
      }),
    onSettled: () => {
      // The appointment card under the confirmation reads these.
      void queryClient.invalidateQueries({
        queryKey: ["public-booking", publicId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["public-booking-calendar", publicId, callId],
      });
    },
  });
}

/** The refusal's code, as the route sends it (`{ code }` on a 409/404/502). */
export const bookingFailureOf = (error: unknown): BookingCalendarFailure =>
  ((error as { code?: string })?.code as BookingCalendarFailure) || "failed";
